import {Archive, ExternalLink, GitFork, Github, Star} from 'lucide-react';
import {memo, ReactElement} from 'react';
import LanguagesStrip from './LanguagesStrip';
import ProjectDescription from './ProjectDescription';
import {Repository} from 'types';
import {getLanguages} from 'utils';

type RepoCardProps = {
  repo: Repository
};

function RepoCard({repo}: RepoCardProps): ReactElement {
  return (
    <div className="repo-card-root">
      <article className="repo-card">
        <a className="repo-card-overlay" href={repo.url} aria-label={`Open ${repo.name} on GitHub`} />
        <LanguagesStrip data={getLanguages(repo)} />
        <div className="content">
          <div className="content-header">
            <div className="icon-root"><Github aria-hidden="true" /></div>
            <div className="title-root">
              <a href={repo.url} className="title">{repo.name}</a>
              {(repo.isFork || repo.isInOrganization) &&
                <p className="subtitle">
                  from <a href={`https://github.com/${repo.nameWithOwner}`}>{repo.nameWithOwner}</a>
                </p>
              }
            </div>
          </div>

          <ProjectDescription
            description={repo.description}
            fallback="A repository from my project archive."
            repo={repo}
          />

          <div className="topics">
            {repo.repositoryTopics.nodes.map((node) => (
              <a key={node.topic.name} href={node.url} className="topic">{node.topic.name}</a>
            ))}
          </div>

          <div className="content-footer">
            <div className="repo-stats">
              {repo.stargazerCount > 0 &&
                <span aria-label={`${repo.stargazerCount} GitHub stars`}>
                  <Star aria-hidden="true" />
                  {repo.stargazerCount}
                </span>
              }

              {repo.forkCount > 0 &&
                <span aria-label={`${repo.forkCount} GitHub forks`}>
                  <GitFork aria-hidden="true" />
                  {repo.forkCount}
                </span>
              }

              {repo.isArchived &&
                <span className="archived-text">
                  <Archive aria-hidden="true" />
                  Archived
                </span>
              }
            </div>

            <div className="repo-links">
              <a className="icon-button" aria-label={`Open ${repo.name} on GitHub`} href={repo.url}>
                <Github aria-hidden="true" />
              </a>

              {repo.homepageUrl &&
                <a
                  className="icon-button"
                  aria-label={`Open ${repo.name} homepage`}
                  href={repo.homepageUrl}
                >
                  <ExternalLink aria-hidden="true" />
                </a>
              }
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

export default memo(RepoCard);
